import React, { useState } from "react";
import { Box, Tooltip } from "@mui/material";
import {
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import CustomTableRow from "../table/CustomTableRow";
import useAuthFetch from "../../hooks/useAuthFetch";
import ConfirmBox from "../ConfirmBox";
import ProfileHistoryModal from "./ProfileHistoryModal";

const formatTimestamp = (ts) => {
  if (!ts) return "-";
  const d = new Date(ts);
  return isNaN(d.getTime()) ? String(ts) : format(d, "dd MMM yyyy, hh:mm a");
};

export default function VerificationProfileTableRow({ row, refetch }) {
  const customFetch = useAuthFetch();
  const navigate = useNavigate();
  const [openErr, setOpenErr] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const [errMsg, setErrMsg] = useState(null);

  const handleDelete = async () => {
    setOpenConfirm(false);
    try {
      const url = new URL(
        `${process.env.REACT_APP_API_URL}/v1/dashboard/verificationProfile/delete`
      );
      const { response, status, message } = await customFetch(url, "POST", {
        profileId: row.profileId,
      });
      if (status !== 200) {
        const errorMessage =
          message ||
          response?.message ||
          "Something went wrong while deleting the verification settings!";
        setErrMsg(errorMessage);
        setOpenErr(true);
        toast.error(errorMessage, { position: "bottom-right", duration: 5000 });
        console.log("Error in deleting verification profile", response);
        return;
      }
      toast.success("Verification settings deleted successfully!", {
        position: "bottom-right",
      });
      refetch();
    } catch (error) {
      console.error("Error deleting verification profile:", error);
      setErrMsg("Something went wrong while deleting the verification settings!");
      setOpenErr(true);
    }
  };

  const ShowButtons = () =>
    JSON.parse(Cookies.get("userDets") || "{}").role === "ADMIN" ? (
      <Box
        sx={{
          display: "flex",
          gap: 1,
        }}
      >
        <Tooltip title="Edit settings" arrow>
          <button
            aria-label="Edit settings"
            onClick={() => navigate(`${row.profileId}`)}
          >
            <EditOutlined className="w-5 h-5 text-black-150" />
          </button>
        </Tooltip>
        <Tooltip title="View change history" arrow>
          <button
            aria-label="View change history"
            onClick={() => setOpenHistory(true)}
          >
            <HistoryOutlined className="w-5 h-5 text-black-150" />
          </button>
        </Tooltip>
        {/* The default profile can be edited but never deleted. */}
        {!row.isDefault && (
          <Tooltip title="Delete settings" arrow>
            <button
              aria-label="Delete settings"
              onClick={() => setOpenConfirm(true)}
            >
              <DeleteOutlined className="w-5 h-5 text-black-150" />
            </button>
          </Tooltip>
        )}
      </Box>
    ) : (
      "-"
    );

  const associationCount = Number(row.associationCount) || 0;

  const NameCell = () => (
    <span className="flex items-center gap-2">
      {row.name}
      {row.isDefault && (
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
          Default
        </span>
      )}
    </span>
  );

  return (
    <>
      <CustomTableRow
        columns={[
          {
            actions: [NameCell],
          },
          {
            // The default's count includes associations with no profile
            // linked, since those use it.
            text: row.isDefault
              ? `${associationCount} (incl. unlinked)`
              : `${associationCount}`,
          },
          {
            text: formatTimestamp(row.updatedAt),
          },
          {
            actions: [ShowButtons],
          },
        ]}
      ></CustomTableRow>
      <ProfileHistoryModal
        open={openHistory}
        setOpen={setOpenHistory}
        profileId={row.profileId}
        profileName={row.name}
      />
      <ConfirmBox
        open={openErr}
        setOpen={setOpenErr}
        buttonText={"Close"}
        text={errMsg ? errMsg : "An error occurred"}
      />
      <ConfirmBox
        open={openConfirm}
        setOpen={setOpenConfirm}
        buttonText={"Confirm"}
        buttonHandler={() => {
          handleDelete();
        }}
        extraButtonText={"Cancel"}
        extraButtonHandler={() => {
          setOpenConfirm(false);
        }}
        text={
          associationCount > 0
            ? `"${row.name}" is linked to ${associationCount} association(s). Are you sure you want to delete it?`
            : `Are you sure you want to delete "${row.name}"?`
        }
      />
    </>
  );
}
